<template>
  <div class="modern-register-page">
    <div class="register-container">
      <div class="register-wrapper">
        <!-- Left Side - Branding -->
        <div class="branding-section">
          <div class="branding-content">
            <div class="logo-container">
              <img src="@/assets/images/logo.png" alt="Ovvura" class="brand-logo" />
            </div>
            <h1 class="brand-title">Welcome to Ovvura</h1>
            <p class="brand-subtitle">Create your account and start your journey with us today.</p>
            
            <div class="features-list">
              <div class="feature-item">
                <div class="feature-icon">
                  <i class="mdi mdi-check-circle"></i>
                </div>
                <div class="feature-text">
                  <h4>Quick Setup</h4>
                  <p>Get started in minutes with our simple registration</p>
                </div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">
                  <i class="mdi mdi-shield-check"></i>
                </div>
                <div class="feature-text">
                  <h4>Secure Platform</h4>
                  <p>Your data is protected with industry-standard security</p>
                </div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">
                  <i class="mdi mdi-star"></i>
                </div>
                <div class="feature-text">
                  <h4>Premium Features</h4>
                  <p>Access powerful tools to grow your business</p>
                </div>
              </div>
            </div>

            <div class="existing-user">
              <router-link to="/login" class="login-link">
                <i class="mdi mdi-login"></i>
                Sign in instead
              </router-link>
            </div>
          </div>
        </div>

        <!-- Right Side - Registration Form -->
        <div class="form-section">
          <div class="form-header">
            <router-link to="/" class="back-link">
              <i class="mdi mdi-arrow-left"></i>
              Back Home
            </router-link>
            <h2 class="form-title">Create Account</h2>
            <p class="form-subtitle">Fill in your details to get started</p>
          </div>

          <!-- Alert Messages -->
          <div v-if="success" class="alert alert-success">
            <i class="mdi mdi-check-circle"></i>
            {{ success }}
          </div>
          <div v-if="generalError" class="alert alert-danger">
            <i class="mdi mdi-alert-circle"></i>
            {{ generalError }}
          </div>

          <b-form class="registration-form" @submit.prevent="register">
            <!-- Personal Information Section -->
            <div class="form-section-group">
              <h3 class="section-title">
                <i class="mdi mdi-account"></i>
                Personal Information
              </h3>
              
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">
                    First Name <span class="required">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-icon">
                      <i class="mdi mdi-account-outline"></i>
                    </span>
                    <input 
                      type="text" 
                      v-model="first_name" 
                      class="form-control" 
                      :class="{ 'is-invalid': errors.first_name }"
                      placeholder="Enter first name" 
                    />
                  </div>
                  <small v-if="errors.first_name" class="error-text">
                    {{ errors.first_name[0] }}
                  </small>
                </div>

                <div class="form-group">
                  <label class="form-label">
                    Last Name <span class="required">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-icon">
                      <i class="mdi mdi-account-outline"></i>
                    </span>
                    <input 
                      type="text" 
                      v-model="last_name" 
                      class="form-control" 
                      :class="{ 'is-invalid': errors.last_name }"
                      placeholder="Enter last name" 
                    />
                  </div>
                  <small v-if="errors.last_name" class="error-text">
                    {{ errors.last_name[0] }}
                  </small>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">
                    Gender <span class="required">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-icon">
                      <i class="mdi mdi-gender-male-female"></i>
                    </span>
                    <select 
                      class="form-control" 
                      v-model="gender"
                      :class="{ 'is-invalid': errors.gender }"
                    >
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <small v-if="errors.gender" class="error-text">
                    {{ errors.gender[0] }}
                  </small>
                </div>

                <div class="form-group">
                  <label class="form-label">
                    Telephone <span class="required">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-icon">
                      <i class="mdi mdi-phone"></i>
                    </span>
                    <input 
                      type="number" 
                      v-model="telephone" 
                      class="form-control" 
                      :class="{ 'is-invalid': errors.contact }"
                      placeholder="Enter phone number" 
                    />
                  </div>
                  <small v-if="errors.contact" class="error-text">
                    {{ errors.contact[0] }}
                  </small>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">
                    Nationality <span class="optional">(Optional)</span>
                  </label>
                  <div class="input-group">
                    <span class="input-icon">
                      <i class="mdi mdi-earth"></i>
                    </span>
                    <input 
                      type="text" 
                      v-model="nationality" 
                      class="form-control" 
                      placeholder="Enter nationality" 
                    />
                  </div>
                  <small v-if="errors.nationality" class="error-text">
                    {{ errors.nationality[0] }}
                  </small>
                </div>

                <div class="form-group">
                  <label class="form-label">
                    Town / City <span class="required">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-icon">
                      <i class="mdi mdi-map-marker"></i>
                    </span>
                    <input 
                      type="text" 
                      v-model="address" 
                      class="form-control" 
                      :class="{ 'is-invalid': errors.address }"
                      placeholder="Enter city" 
                    />
                  </div>
                  <small v-if="errors.address" class="error-text">
                    {{ errors.address[0] }}
                  </small>
                </div>
              </div>
            </div>

            <!-- Account Security Section -->
            <div class="form-section-group">
              <h3 class="section-title">
                <i class="mdi mdi-lock"></i>
                Account Security
              </h3>
              
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">
                    Email Address <span class="required">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-icon">
                      <i class="mdi mdi-email-outline"></i>
                    </span>
                    <input 
                      type="email" 
                      v-model="email" 
                      class="form-control" 
                      :class="{ 'is-invalid': errors.email }"
                      placeholder="Enter email address" 
                    />
                  </div>
                  <small v-if="errors.email" class="error-text">
                    {{ errors.email[0] }}
                  </small>
                </div>

                <div class="form-group">
                  <label class="form-label">
                    Password <span class="required">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-icon">
                      <i class="mdi mdi-lock-outline"></i>
                    </span>
                    <input 
                      type="password" 
                      v-model="password" 
                      class="form-control" 
                      :class="{ 'is-invalid': errors.password }"
                      placeholder="Create password" 
                    />
                  </div>
                  <small v-if="errors.password" class="error-text">
                    {{ errors.password[0] }}
                  </small>
                </div>
              </div>
            </div>

            <!-- Account Type Section -->
            <div class="form-section-group">
              <h3 class="section-title">
                <i class="mdi mdi-account-group"></i>
                Account Type
              </h3>
              
              <div class="account-type-selector">
                <label class="account-type-card" :class="{ 'active': user_type === '1' }">
                  <input type="radio" v-model="user_type" value="1" class="account-radio" />
                  <div class="card-content">
                    <div class="card-icon personal">
                      <i class="mdi mdi-account"></i>
                    </div>
                    <h4>Personal</h4>
                    <p>For individual use and personal bookings</p>
                  </div>
                  <div class="card-checkmark">
                    <i class="mdi mdi-check-circle"></i>
                  </div>
                </label>

                <label class="account-type-card" :class="{ 'active': user_type === '2' }">
                  <input type="radio" v-model="user_type" value="2" class="account-radio" />
                  <div class="card-content">
                    <div class="card-icon business">
                      <i class="mdi mdi-briefcase"></i>
                    </div>
                    <h4>Service Provider</h4>
                    <p>For businesses and service professionals</p>
                  </div>
                  <div class="card-checkmark">
                    <i class="mdi mdi-check-circle"></i>
                  </div>
                </label>
              </div>
              <small v-if="errors.user_type" class="error-text">
                {{ errors.user_type[0] }}
              </small>
            </div>

            <!-- Business Information Section -->
            <transition name="slide-fade">
              <div v-show="user_type === '2'" class="form-section-group business-section">
                <h3 class="section-title">
                  <i class="mdi mdi-briefcase-outline"></i>
                  Business Information
                </h3>
                
                <div class="form-row">
                  <div class="form-group">
                    <label class="form-label">Business Name</label>
                    <div class="input-group">
                      <span class="input-icon">
                        <i class="mdi mdi-store"></i>
                      </span>
                      <input 
                        type="text" 
                        v-model="businessname" 
                        class="form-control" 
                        :class="{ 'is-invalid': errors.businessname }"
                        placeholder="Enter business name" 
                      />
                    </div>
                    <small v-if="errors.businessname" class="error-text">
                      {{ errors.businessname[0] }}
                    </small>
                  </div>

                  <div class="form-group">
                    <label class="form-label">Business Address</label>
                    <div class="input-group">
                      <span class="input-icon">
                        <i class="mdi mdi-map-marker"></i>
                      </span>
                      <input 
                        type="text" 
                        v-model="businessaddress" 
                        class="form-control" 
                        :class="{ 'is-invalid': errors.businessaddress }"
                        placeholder="Enter business address" 
                      />
                    </div>
                    <small v-if="errors.businessaddress" class="error-text">
                      {{ errors.businessaddress[0] }}
                    </small>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group">
                    <label class="form-label">Business Telephone</label>
                    <div class="input-group">
                      <span class="input-icon">
                        <i class="mdi mdi-phone-in-talk"></i>
                      </span>
                      <input 
                        type="number" 
                        v-model="businesstelephone" 
                        class="form-control" 
                        :class="{ 'is-invalid': errors.businesstelephone }"
                        placeholder="Enter business phone" 
                      />
                    </div>
                    <small v-if="errors.businesstelephone" class="error-text">
                      {{ errors.businesstelephone[0] }}
                    </small>
                  </div>

                  <div class="form-group">
                    <label class="form-label">Business Logo</label>
                    <div class="file-upload">
                      <input 
                        type="file" 
                        @change="onLogoChange" 
                        class="file-input" 
                        accept=".jpg,.png,.jpeg" 
                        id="logo-upload"
                      />
                      <label for="logo-upload" class="file-label">
                        <i class="mdi mdi-cloud-upload"></i>
                        <span>Choose logo file</span>
                      </label>
                      <span v-if="businesslogo" class="file-name">{{ businesslogo.name }}</span>
                    </div>
                    <small v-if="errors.businesslogo" class="error-text">
                      {{ errors.businesslogo[0] }}
                    </small>
                  </div>
                </div>

                <div class="form-group full-width">
                  <label class="form-label">
                    What do you do? <span class="required">*</span>
                  </label>
                  <tinymce id="d1" v-model="description"></tinymce>
                  <small v-if="errors.description" class="error-text">
                    {{ errors.description[0] }}
                  </small>
                </div>
              </div>
            </transition>

            <!-- Submit Button -->
            <div class="form-actions">
              <b-button
                :disabled="loader"
                type="submit"
                class="submit-btn"
              >
                <b-spinner v-if="loader" small class="btn-spinner" />
                <i v-else class="mdi mdi-account-plus"></i>
                {{ loader ? 'Creating Account...' : 'Create Account' }}
              </b-button>
            </div>

            <!-- Terms and Privacy -->
            <div class="form-footer">
              <p class="terms-text">
                By creating an account, you agree to our 
                <a href="#" class="link">Terms of Service</a> and 
                <a href="#" class="link">Privacy Policy</a>
              </p>
            </div>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Register",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      description: "",
      first_name: "",
      last_name: "",
      email: "",
      telephone: "",
      nationality: "",
      address: "",
      gender: "Male",
      password: "",
      user_type: "1",
      businessname: "",
      businesstelephone: "",
      businessaddress: "",
      businesslogo: "",
      loader: false,
      success: "",
      generalError: "",
      errors: {},
      options: [
        { text: " Personal", value: "1" },
        { text: " Service Provider", value: "2" },
      ],
    };
  },
  methods: {
    onLogoChange(e) {
      this.businesslogo = e.target.files[0];
    },
    async register() {
      this.loader = true;
      this.errors = {};
      this.success = "";
      this.generalError = "";

      let formData = new FormData();
      formData.append("first_name", this.first_name);
      formData.append("last_name", this.last_name);
      formData.append("email", this.email);
      formData.append("nationality", this.nationality);
      formData.append("gender", this.gender);
      formData.append("password", this.password);
      formData.append("contact", this.telephone);
      formData.append("address", this.address);
      formData.append("user_type", this.user_type);
      formData.append("businessname", this.businessname);
      formData.append("businessaddress", this.businessaddress);
      formData.append("businesstelephone", this.businesstelephone);
      formData.append("businesslogo", this.businesslogo);
      formData.append("description", this.description);

      const coords = JSON.parse(localStorage.getItem("coordinates"));
      if (coords) {
        formData.append("longitude", coords.lng);
        formData.append("latitude", coords.lat);
      }

      try {
        const response = await axios.post("/register", formData);
        if (response.status === 200) {
          this.success = response.data.message;
          this.resetForm();
          setTimeout(() => this.login(), 2000);
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
          this.generalError = "Please fix the highlighted fields.";
        } else {
          this.generalError =
            error.response?.data?.message || "Registration failed. Try again.";
        }
      } finally {
        this.loader = false;
      }
    },
    resetForm() {
      this.first_name = "";
      this.last_name = "";
      this.nationality = "";
      this.telephone = "";
      this.address = "";
      this.businessname = "";
      this.businesstelephone = "";
      this.businessaddress = "";
      this.businesslogo = "";
      this.password = "";
      this.description = "";
    },
    async login() {
      try {
        const formData = new FormData();
        formData.append("email", this.email);
        formData.append("password", this.password);
        const response = await axios.post("/login", formData);
        if (response.status == 200) {
          const userData = response.data.user;
          const token = response.data.token;
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          localStorage.setItem("user", JSON.stringify(userData));
          localStorage.setItem("token", token);
          this.$router.push(this.$route.query.redirectFrom || { name: "dashboard" });
        } else {
          this.generalError = response.data.message;
        }
      } catch (err) {
        this.generalError = "Login failed. Please check your credentials.";
      }
    },
  },
};
</script>

<style scoped>
/* Modern Registration Page Styles */
.modern-register-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #3A2B78FF 0%, #d4f5f7 100%);
  padding: 2rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.register-container {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
}

.register-wrapper {
  display: grid;
  grid-template-columns: 400px 1fr;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-height: 90vh;
}

@media (max-width: 1024px) {
  .register-wrapper {
    grid-template-columns: 1fr;
  }
}

/* Branding Section */
.branding-section {
  background: linear-gradient(135deg, #3A2B78FF 0%, #3A2B78FF 100%);
  padding: 1.5rem 2rem;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

@media (max-width: 1024px) {
  .branding-section {
    padding: 2rem;
  }
}

.branding-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.logo-container {
  margin-bottom: 2rem;
}

.brand-logo {
  height: 64px;
  width: auto;
  filter: brightness(0) invert(1);
}

.brand-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  line-height: 1.2;
  color: rgb(208, 223, 227);
}

.brand-subtitle {
  font-size: 0.9rem;
  opacity: 0.6;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.features-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.0rem;
  margin-bottom: 2rem;
}

.feature-item {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.feature-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.feature-text h4 {
  color: rgb(159, 157, 157);
  font-size: 1.0rem;
  font-weight: 400;
  margin: 0 0 0.25rem 0;
}

.feature-text p {
  font-size: 0.6rem;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
}

.existing-user {
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.existing-user p {
  margin: 0 0 0.75rem 0;
  font-size: 0.95rem;
  opacity: 0.9;
}

.login-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  padding: 0.5rem 0;
  transition: all 0.3s ease;
}

.login-link:hover {
  gap: 0.75rem;
  color: white;
}

/* Form Section */
.form-section {
  padding: 3rem;
  overflow-y: auto;
  max-height: 90vh;
}

@media (max-width: 768px) {
  .form-section {
    padding: 2rem 1.5rem;
  }
}

.form-header {
  margin-bottom: 2rem;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #3A2B78FF;
  font-weight: 400;
  text-decoration: none;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.back-link:hover {
  gap: 0.75rem;
  color: #290088FF;
}

.form-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.form-subtitle {
  font-size: 1rem;
  color: #191919FF;
  margin: 0;
}

/* Alerts */
.alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  border: none;
}

.alert i {
  font-size: 1.25rem;
}

.alert-success {
  background: #d4edda;
  color: #155724;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
}

/* Form Sections */
.registration-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-section-group {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 16px;
}

.business-section {
  background: #fff8e6;
  border: 2px solid #ffd700;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 400;
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
}

.section-title i {
  font-size: 1.5rem;
  color: #3A2B78FF;
}

/* Form Rows and Groups */
.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-weight: 500;
  color: #151515FF;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.required {
  color: #dc3545;
  font-weight: 600;
}

.optional {
  color: #6c757d;
  font-weight: 400;
  font-size: 0.85rem;
}

/* Input Groups */
.input-group {
  position: relative;
  display: flex;
  align-items: stretch;
}

.input-icon {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3A2B78FF;
  font-size: 1.2rem;
  z-index: 2;
}

.form-control {
  flex: 1;
  padding: 0.75rem 1rem 0.75rem 3rem !important;
  border: 2px solid #e1e8ed !important;
  border-radius: 10px !important;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background: white;
}

.form-control:focus {
  border-color: #3A2B78FF !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 67, 168, 0.1) !important;
  outline: none;
}

.form-control.is-invalid {
  border-color: #dc3545 !important;
}

.form-control.is-invalid:focus {
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.1) !important;
}

.error-text {
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.4rem;
  display: block;
}

/* Account Type Selector */
.account-type-selector {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 0.5rem;
}

@media (max-width: 768px) {
  .account-type-selector {
    grid-template-columns: 1fr;
  }
}

.account-type-card {
  position: relative;
  background: white;
  border: 2px solid #e1e8ed;
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.account-type-card:hover {
  border-color: #3A2B78FF;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 168, 168, 0.15);
}

.account-type-card.active {
  border-color: #3A2B78FF;
  background: linear-gradient(135deg, #3A2B78FF 0%, #3A2B78FF 100%);
  box-shadow: 0 4px 12px rgba(8, 0, 168, 0.2);
}

.account-radio {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.card-content {
  flex: 1;
}

.card-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: white;
}

.card-icon.personal {
  background: linear-gradient(135deg, #3A2B78FF 0%, #3A2B78FF 100%);
}

.card-icon.business {
  background: linear-gradient(135deg, #ffd700 0%, #ffb700 100%);
}

.card-content h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.card-content p {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
}

.card-checkmark {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e1e8ed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  transition: all 0.3s ease;
}

.account-type-card.active .card-checkmark {
  background: #3A2B78FF;
  transform: scale(1.1);
}

/* File Upload */
.file-upload {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-input {
  display: none;
}

.file-label {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: white;
  border: 2px dashed #3A2B78FF;
  border-radius: 10px;
  color: #3A2B78FF;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0;
}

.file-label:hover {
  background: #E6FDFF;
  border-color: #3A2B78FF;
}

.file-label i {
  font-size: 1.3rem;
}

.file-name {
  font-size: 0.85rem;
  color: #6c757d;
  padding-left: 0.5rem;
}

/* Submit Button */
.form-actions {
  margin-top: 1rem;
}

.submit-btn {
  width: 100%;
  padding: 1rem 1rem !important;
  background: linear-gradient(135deg, #3A2B78FF 0%, #3A2B78FF 100%) !important;
  border: none !important;
  border-radius: 12px !important;
  color: white !important;
  font-size: 1.05rem !important;
  font-weight: 600 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.75rem !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 12px rgba(0, 22, 168, 0.3) !important;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 70, 168, 0.4) !important;
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.submit-btn i {
  font-size: 1.3rem;
}

.btn-spinner {
  margin-right: 0.5rem;
}

/* Form Footer */
.form-footer {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
  text-align: center;
}

.terms-text {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.6;
}

.terms-text .link {
  color: #3A2B78FF;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.terms-text .link:hover {
  color: #001088FF;
  text-decoration: underline;
}

/* Animations */
.slide-fade-enter-active {
  transition: all 0.4s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(-20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Scrollbar Styling */
.form-section::-webkit-scrollbar {
  width: 8px;
}

.form-section::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.form-section::-webkit-scrollbar-thumb {
  background: #3A2B78FF;
  border-radius: 10px;
}

.form-section::-webkit-scrollbar-thumb:hover {
  background: #002088FF;
}

/* Responsive Adjustments */
@media (max-width: 1024px) {
  .branding-section {
    display: none;
  }
  
  .register-wrapper {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .modern-register-page {
    padding: 1rem;
  }

  .register-wrapper {
    border-radius: 16px;
    min-height: auto;
  }

  .form-section {
    padding: 1.5rem;
    max-height: none;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .account-type-selector {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .submit-btn {
    padding: 0.875rem 1.5rem !important;
    font-size: 1rem !important;
  }
}

@media (max-width: 480px) {
  .form-title {
    font-size: 1.3rem;
  }

  .brand-title {
    font-size: 1.5rem;
  }

  .feature-item {
    gap: 0.75rem;
  }

  .feature-icon {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }

  .form-section-group {
    padding: 1rem;
  }
}

/* Print Styles */
@media print {
  .modern-register-page {
    background: white;
  }

  .branding-section {
    display: none;
  }

  .back-link,
  .form-footer,
  .existing-user {
    display: none;
  }
}

/* High Contrast Mode Support */
@media (prefers-contrast: high) {
  .form-control {
    border-width: 3px !important;
  }

  .account-type-card {
    border-width: 3px;
  }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Dark Mode Support (Optional) */
@media (prefers-color-scheme: dark) {
  .modern-register-page {
    background: linear-gradient(135deg, #1A0F45FF 0%, #1A1338FF 100%);
  }

  .register-wrapper {
    background: #FFFFFF;
  }

  .form-section {
    color: #0A0A0AFF;
  }

  .form-title,
  .section-title,
  .card-content h4 {
    color: #0C0C0CFF;
  }

  .form-subtitle,
  .form-label,
  .card-content p {
    color: #b0b0b0;
  }

  .form-control {
    background: #FDFDFDFF !important;
    border-color: #5A5959FF !important;
    color: #000000FF !important;
  }

  .form-section-group {
    background: #F6FBFEFF;
  }

  .account-type-card {
    background: #DFF0F5FF;
    border-color: #615F5FFF;
  }

  .account-type-card.active {
    background: #3A2B78FF;
  }
}

/* Focus Visible for Accessibility */
*:focus-visible {
  outline: 3px solid #3A2B78FF;
  outline-offset: 2px;
}

/* Select Dropdown Styling */
select.form-control {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%233A2B78FF' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 16px 12px;
  appearance: none;
  padding-right: 3rem !important;
}

/* Placeholder Styling */
.form-control::placeholder {
  color: #171818FF;
  opacity: 1;
}

.form-control:focus::placeholder {
  opacity: 0.5;
}

/* Loading State */
.submit-btn .b-spinner {
  width: 1.2rem;
  height: 1.2rem;
}

/* Success State Animation */
@keyframes successPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(21, 31, 87, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(21, 32, 87, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(21, 45, 87, 0);
  }
}

.alert-success {
  animation: successPulse 2s infinite;
}

/* Error State Animation */
@keyframes errorShake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
}

.form-control.is-invalid {
  animation: errorShake 0.5s ease-in-out;
}

/* Tooltip for Input Hints */
.input-hint {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 0.9rem;
  cursor: help;
}

/* Better Select Appearance on Mobile */
@media (max-width: 768px) {
  select.form-control {
    font-size: 16px; /* Prevents zoom on iOS */
  }
}

/* Loading Overlay (Optional Enhancement) */
.form-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Input Number Spinner Remove */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>